const UNSAFE_PATH_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);
const MAX_ARRAY_INDEX = 1_000_000;
export class SetDataRuntimeError extends Error {
    code;
    path;
    remediation;
    constructor(code, path, message, remediation) {
        super(`${code} ${message}`);
        this.name = 'SetDataRuntimeError';
        this.code = code;
        this.path = path;
        this.remediation = remediation;
    }
}
function pathError(path, detail) {
    return new SetDataRuntimeError('WF7101', path, `Invalid setData path ${JSON.stringify(path)}: ${detail}`, 'Use a non-empty dot path with non-negative numeric bracket indices, such as user.name or items[0].selected.');
}
function parseProperty(path, offset) {
    let end = offset;
    while (end < path.length && path[end] !== '.' && path[end] !== '[' && path[end] !== ']') {
        end += 1;
    }
    const property = path.slice(offset, end);
    if (property.length === 0 || property.trim() !== property || /\s/u.test(property)) {
        throw pathError(path, 'property segments must be non-empty and contain no whitespace');
    }
    if (UNSAFE_PATH_SEGMENTS.has(property)) {
        throw new SetDataRuntimeError('WF7102', path, `Unsafe setData path segment is forbidden: ${property}`, 'Rename the data field and never write __proto__, prototype, or constructor through setData.');
    }
    return { property, end };
}
export function parseSetDataPath(path) {
    if (path.length === 0 || path.trim() !== path) {
        throw pathError(path, 'the path must not be empty or surrounded by whitespace');
    }
    const first = parseProperty(path, 0);
    const segments = [first.property];
    let offset = first.end;
    while (offset < path.length) {
        const character = path[offset];
        if (character === '.') {
            const parsed = parseProperty(path, offset + 1);
            segments.push(parsed.property);
            offset = parsed.end;
            continue;
        }
        if (character !== '[') {
            throw pathError(path, `unexpected character ${JSON.stringify(character)}`);
        }
        const closing = path.indexOf(']', offset + 1);
        if (closing === -1) {
            throw pathError(path, 'a bracket index is missing its closing bracket');
        }
        const indexText = path.slice(offset + 1, closing);
        if (indexText.length === 0 || [...indexText].some((digit) => digit < '0' || digit > '9')) {
            throw pathError(path, 'bracket segments must contain one non-negative integer');
        }
        const index = Number(indexText);
        if (!Number.isSafeInteger(index) || index > MAX_ARRAY_INDEX) {
            throw new SetDataRuntimeError('WF7104', path, `setData array index exceeds the runtime limit: ${indexText}`, `Use an array index from 0 through ${MAX_ARRAY_INDEX}, or replace the complete array value explicitly.`);
        }
        segments.push(index);
        offset = closing + 1;
    }
    return Object.freeze(segments);
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || prototype === Object.prototype;
}
function cloneRecord(value) {
    return Object.create(Object.getPrototypeOf(value), Object.getOwnPropertyDescriptors(value));
}
function cloneArray(value) {
    const clone = new Array(value.length);
    for (const key of Reflect.ownKeys(value)) {
        if (key !== 'length') {
            const descriptor = Object.getOwnPropertyDescriptor(value, key);
            if (descriptor !== undefined) {
                Object.defineProperty(clone, key, descriptor);
            }
        }
    }
    return clone;
}
function ownDataValue(container, segment, path) {
    const descriptor = Object.getOwnPropertyDescriptor(container, segment);
    if (descriptor === undefined) {
        return undefined;
    }
    if (!('value' in descriptor)) {
        throw new SetDataRuntimeError('WF7103', path, `setData cannot traverse an accessor property at segment ${JSON.stringify(segment)}`, 'Keep page and component data JSON-like, using plain objects, arrays, and data properties.');
    }
    return descriptor.value;
}
function incompatibleContainer(path, segment) {
    return new SetDataRuntimeError('WF7103', path, `setData cannot traverse the existing value before segment ${JSON.stringify(segment)}`, 'Replace the parent with an object or array in an earlier setData call, or correct the path to match the current data shape.');
}
function childContainer(current, nextSegment, path) {
    if (current === undefined) {
        return typeof nextSegment === 'number' ? [] : {};
    }
    if (typeof nextSegment === 'number') {
        if (!Array.isArray(current)) {
            throw incompatibleContainer(path, nextSegment);
        }
        return cloneArray(current);
    }
    if (!isPlainRecord(current)) {
        throw incompatibleContainer(path, nextSegment);
    }
    return cloneRecord(current);
}
function assertWritable(container, segment, path) {
    const descriptor = Object.getOwnPropertyDescriptor(container, segment);
    if ((descriptor === undefined && !Object.isExtensible(container))
        || (descriptor !== undefined && (!('value' in descriptor) || descriptor.writable !== true))) {
        throw new SetDataRuntimeError('WF7103', path, `setData target is not a writable data property at segment ${JSON.stringify(segment)}`, 'Use extensible plain data objects and writable array entries for setData targets.');
    }
}
function writeSegment(container, segment, value, path) {
    assertWritable(container, segment, path);
    if (!Reflect.set(container, segment, value, container)) {
        throw incompatibleContainer(path, segment);
    }
}
function applyPath(root, path, segments, value) {
    let container = root;
    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];
        const nextSegment = segments[index + 1];
        if (segment === undefined || nextSegment === undefined) {
            throw pathError(path, 'the parsed path is incomplete');
        }
        const child = childContainer(ownDataValue(container, segment, path), nextSegment, path);
        writeSegment(container, segment, child, path);
        container = child;
    }
    const finalSegment = segments[segments.length - 1];
    if (finalSegment === undefined) {
        throw pathError(path, 'the parsed path is empty');
    }
    writeSegment(container, finalSegment, value, path);
}
function patchEntries(patch) {
    if (!isPlainRecord(patch)) {
        throw new SetDataRuntimeError('WF7106', '<patch>', 'setData patch must be a plain object', 'Pass one plain object whose keys are setData paths and whose values are replacement data.');
    }
    return Object.keys(patch).map((path) => {
        const descriptor = Object.getOwnPropertyDescriptor(patch, path);
        if (descriptor === undefined || !('value' in descriptor)) {
            throw new SetDataRuntimeError('WF7106', path, 'setData patch properties must be direct data values, not accessors', 'Replace getter or setter properties with plain enumerable data properties before calling setData.');
        }
        if (descriptor.value === undefined) {
            throw new SetDataRuntimeError('WF7105', path, `setData does not accept undefined for path ${JSON.stringify(path)} in the M2 compatibility profile`, 'Use null, delete through an explicit product operation, or select a later compatibility profile with defined undefined semantics.');
        }
        return [path, descriptor.value];
    });
}
export function applySetDataPatch(data, patch) {
    if (!isPlainRecord(data)) {
        throw new SetDataRuntimeError('WF7106', '<data>', 'setData requires a plain object data root', 'Initialize page or component data with a plain object before creating its runtime instance.');
    }
    const working = cloneRecord(data);
    const touched = new Map();
    for (const [path, value] of patchEntries(patch)) {
        const segments = parseSetDataPath(path);
        const rootSegment = segments[0];
        if (typeof rootSegment !== 'string') {
            throw pathError(path, 'the root segment must be a property name');
        }
        applyPath(working, path, segments, value);
        touched.set(rootSegment, path);
    }
    for (const [segment, path] of touched) {
        assertWritable(data, segment, path);
    }
    for (const [segment, path] of touched) {
        writeSegment(data, segment, ownDataValue(working, segment, path), path);
    }
}
export function createSetDataStore(data, options = {}) {
    const flush = options.flush ?? (() => undefined);
    let queue = Promise.resolve();
    let latest = queue;
    return Object.freeze({
        data,
        setData(patch, callback) {
            applySetDataPatch(data, patch);
            const operation = queue.then(async () => {
                await flush();
                callback?.();
            });
            latest = operation;
            queue = operation.then(() => undefined, () => undefined);
        },
        whenSettled() {
            return latest;
        },
    });
}
//# sourceMappingURL=set-data.js.map